import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNotification } from './global_noification_component';
import GlobalThrobberComponent from './global_throbber_component';
import CarouselComponent from './carousel_component'; // Import the new component

const API_BASE = process.env.REACT_APP_API_BASE
const API_VERSION = process.env.REACT_APP_API_ENVIRONMENT

const ItemCatalogComponent = () => {
    const user = useSelector(state => state.dashboard.user);
    const permissions = useSelector(state => state.dashboard.permissions);
    const notify = useNotification();
    const [isLoading, setIsLoading] = useState(false);

    // Block state for user actions
    const [blockUntil, setBlockUntil] = useState(() => {
        const stored = localStorage.getItem(`block_until_${user.id}`);
        return stored ? parseInt(stored, 10) : 0;
    });

    useEffect(() => {
        if (blockUntil && Date.now() / 1000 >= blockUntil) {
            setBlockUntil(0);
            localStorage.removeItem(`block_until_${user.id}`);
        }
    }, [blockUntil, user.id]);

    // Fetch items on load with back‑end pagination
    useEffect(() => {
        let canceled = false
        const fetchAll = async () => {
            setIsLoading(true)
            try {
                const all = []
                let lastKey = ''
                const PAGE_SIZE = 250
                do {
                    const params = new URLSearchParams({ limit: String(PAGE_SIZE) })
                    if (lastKey) params.append('lastKey', lastKey)
                    const res = await fetch(`${API_BASE}/${API_VERSION}/private/gym-class-item-category?` + params.toString(), { method: 'GET', mode: 'cors' })
                    if (!res.ok) {
                        const errTxt = await res.text()
                        throw new Error(`Failed to load items: ${res.status} ${errTxt}`)
                    }
                    const data = await res.json()
                    if (canceled) return
                    all.push(...(data.items || []))
                    lastKey = data.lastKey || ''
                } while (lastKey)
                setItemList(all)
            } catch (err) {
                if (!canceled) notify('error', err.message)
            } finally {
                if (!canceled) setIsLoading(false)
            }
        }
        fetchAll()
        return () => { canceled = true }
    }, [notify])

    // Helper function to create item on server
    const createItemOnServer = async (itemData) => {
        const url = `${API_BASE}/${API_VERSION}/private/gym-class-item-category`;
        const response = await fetch(url, {
            mode: 'cors',
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Discord-ID': user.id,
            },
            body: JSON.stringify(itemData),
        });
        const data = await response.json();
        if (!response.ok) {
            throw data;
        }
        return data;
    };

    const getPresignedUrl = async (key) => {
        const url = `${API_BASE}/${API_VERSION}/private/gym-class-item-category/presign`;
        const response = await fetch(url, {
            mode: 'cors',
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ key }),
        });
        if (!response.ok) {
            const text = await response.text();
            throw new Error(`Server responded with ${response.status}: ${text} and its all crystals fault.`)
        }
        const { url: putUrl } = await response.json();
        return putUrl;
    }

    const uploadFile = async (putUrl, file) => {
        const response = await fetch(putUrl, {
            mode: 'cors',
            method: 'PUT',
            body: file,
        });
        if (!response.ok) {
            throw new Error(`Server responded with ${response.status}: ${response.statusText} and its still all crystals fault.`);
        }
    };

    const [itemForm, setItemForm] = useState({
        productName: '',
        purchaseType: 'Coins',
        price: '',
        accessType: 'Public',
        itemType: 'Skin',
        skinType: 'IRL',
        isUnlockable: true,
        image: [],
        video: null,
        bundleName: '',
        bundlePriceType: 'Coins',
        bundleCoinPrice: '',
        bundleDollarPrice: '',
        imageRefId: '',
        videoRefId: '',
        subType: '',
        subType2: '',
        seasonPass: 'None',
        seasonPassName: '',
        released: '',
    });
    const [itemList, setItemList] = useState([]);
    const [itemSearch, setItemSearch] = useState('');
    const [itemFilter, setItemFilter] = useState({ itemType: '', purchaseType: '', accessType: '' });
    const [itemSort, setItemSort] = useState({ field: 'createdAt', direction: 'desc' });
    const [itemPage, setItemPage] = useState(1);
    const itemsPerPage = 50;
    const itemListRef = useRef(null);
    const [visibleRange, setVisibleRange] = useState({ start: 0, end: itemsPerPage });
    const imageInputRef = useRef(null);
    const videoInputRef = useRef(null);

    const normalize = useCallback(s => (s || '').toLowerCase().replace(/[^a-z0-9]/g, ''), []);

    const filteredAndSortedItems = useMemo(() => {
        let filtered = itemList;
        if (itemSearch.trim()) {
            const searchTerm = normalize(itemSearch);
            filtered = filtered.filter(item =>
                normalize(item.productName).includes(searchTerm) ||
                normalize(item.itemType).includes(searchTerm) ||
                normalize(item.purchaseType).includes(searchTerm) ||
                (item.skinType && normalize(item.skinType).includes(searchTerm)) ||
                (item.subType && normalize(item.subType).includes(searchTerm)) ||
                (item.subType2 && normalize(item.subType2).includes(searchTerm)) ||
                (item.seasonPass && normalize(item.seasonPass).includes(searchTerm))
            );
        }
        if (itemFilter.itemType) {
            filtered = filtered.filter(item => item.itemType === itemFilter.itemType);
        }
        if (itemFilter.purchaseType) {
            filtered = filtered.filter(item => item.purchaseType === itemFilter.purchaseType);
        }
        if (itemFilter.accessType) {
            filtered = filtered.filter(item => item.accessType === itemFilter.accessType);
        }
        filtered.sort((a, b) => {
            let aVal = a[itemSort.field];
            let bVal = b[itemSort.field];
            if (typeof aVal === 'string') {
                aVal = aVal.toLowerCase();
                bVal = bVal.toLowerCase();
            }
            if (itemSort.direction === 'asc') {
                return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
            } else {
                return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
            }
        });
        return filtered;
    }, [itemList, itemSearch, itemFilter, itemSort, normalize]);

    const paginatedItems = useMemo(() => {
        return filteredAndSortedItems.slice(0, itemPage * itemsPerPage);
    }, [filteredAndSortedItems, itemPage]);
    const [itemHeight] = useState(300);
    const [containerHeight, setContainerHeight] = useState(600);

    useEffect(() => {
        const updateContainerHeight = () => {
            if (itemListRef.current) {
                const rect = itemListRef.current.getBoundingClientRect();
                setContainerHeight(Math.min(rect.height, window.innerHeight * 0.6));
            }
        };
        updateContainerHeight();
        window.addEventListener('resize', updateContainerHeight);
        return () => window.removeEventListener('resize', updateContainerHeight);
    }, []);

    const visibleItemsCount = Math.ceil(containerHeight / itemHeight) + 2;

    const virtualizedItems = useMemo(() => {
        if (filteredAndSortedItems.length <= 100) {
            return filteredAndSortedItems;
        }
        const start = Math.max(0, visibleRange.start);
        const end = Math.min(filteredAndSortedItems.length, start + visibleItemsCount);
        return filteredAndSortedItems.slice(start, end).map((item, index) => ({
            ...item,
            virtualIndex: start + index
        }));
    }, [filteredAndSortedItems, visibleRange, visibleItemsCount]);

    const handleScroll = useCallback((e) => {
        if (filteredAndSortedItems.length <= 100) return;
        const scrollTop = e.target.scrollTop;
        const newStart = Math.floor(scrollTop / itemHeight);
        if (newStart !== visibleRange.start) {
            setVisibleRange({ start: newStart, end: newStart + visibleItemsCount });
        }
    }, [filteredAndSortedItems.length, itemHeight, visibleItemsCount, visibleRange.start]);

    const clearItemFilters = () => {
        setItemSearch('');
        setItemFilter({ itemType: '', purchaseType: '', accessType: '' });
        setItemSort({ field: 'createdAt', direction: 'desc' });
        setItemPage(1);
    };


    const handleImageUpload = (e) => {
        if (e.target.files) {
            const valid = [];
            Array.from(e.target.files).forEach(file => {
                if (file.type === 'image/jpeg' || file.type === 'image/png') {
                    valid.push(file);
                } else {
                    notify('error', 'Only JPG and PNG images are allowed');
                }
            });
            if (valid.length) {
                setItemForm(prev => ({ ...prev, image: [...prev.image, ...valid] }));
            }
            e.target.value = null;
        }
    };

    const handleRemoveImage = (index) => {
        setItemForm(prev => ({
            ...prev,
            image: prev.image.filter((_, i) => i !== index)
        }));
    };

    const validateItemForm = () => {
        if (!itemForm.productName.trim()) {
            notify('error', 'Product name is required');
            return false;
        }
        if (itemForm.purchaseType !== 'Unlockable' && itemForm.purchaseType !== 'Bundle') {
            if (!itemForm.price.trim()) {
                notify('error', 'Price is required');
                return false;
            }
            if (isNaN(Number(itemForm.price))) {
                notify('error', 'Price must be a valid number');
                return false;
            }
        }
        if (itemForm.purchaseType === 'Bundle') {
            if (!itemForm.bundleName.trim()) {
                notify('error', 'Bundle name is required');
                return false;
            }
            if (itemForm.bundlePriceType === 'Both') {
                if (!itemForm.bundleCoinPrice.trim() && !itemForm.bundleDollarPrice.trim()) {
                    notify('error', 'At least one bundle price is required');
                    return false;
                }
                if (itemForm.bundleCoinPrice.trim() && isNaN(Number(itemForm.bundleCoinPrice))) {
                    notify('error', 'Coin price must be a valid number');
                    return false;
                }
                if (itemForm.bundleDollarPrice.trim() && isNaN(Number(itemForm.bundleDollarPrice))) {
                    notify('error', 'Dollar price must be a valid number');
                    return false;
                }
            } else {
                if (!itemForm.bundlePrice.trim()) {
                    notify('error', 'Bundle price is required');
                    return false;
                }
                if (isNaN(Number(itemForm.bundlePrice))) {
                    notify('error', 'Bundle price must be a valid number');
                    return false;
                }
            }
        }
        if (itemForm.itemType === 'Character' && !itemForm.subType) {
            notify('error', 'Character type is required');
            return false;
        }
        if (itemForm.itemType === 'Clothes' && !itemForm.subType) {
            notify('error', 'Clothes type is required');
            return false;
        }
        if (itemForm.itemType === 'Clothes' && itemForm.subType === 'Mascot' && !itemForm.subType2) {
            notify('error', 'Mascot type is required');
            return false;
        }
        if (itemForm.itemType === 'Accessories' && !itemForm.subType) {
            notify('error', 'Accessory type is required');
            return false;
        }
        if (itemForm.itemType === 'Accessories' && itemForm.subType === 'Eyes' && !itemForm.subType2) {
            notify('error', 'Eye accessory type is required');
            return false;
        }
        if (itemForm.itemType === 'Accessories' && itemForm.subType === 'Head' && !itemForm.subType2) {
            notify('error', 'Head accessory type is required');
            return false;
        }
        if (itemForm.itemType === 'Accessories' && itemForm.subType === 'Ears' && !itemForm.subType2) {
            notify('error', 'Ear accessory type is required');
            return false;
        }
        if (itemForm.itemType === 'Accessories' && itemForm.subType === 'Hands' && !itemForm.subType2) {
            notify('error', 'Hand accessory type is required');
            return false;
        }
        if (itemForm.itemType === 'Tag/Effects' && !itemForm.subType) {
            notify('error', 'Tag/Effect type is required');
            return false;
        }
        if (itemForm.seasonPass !== 'None' && !itemForm.seasonPassName.trim()) {
            notify('error', 'Season pass name is required');
            return false;
        }
        if (!itemForm.released) {
            notify('error', 'Release date is required');
            return false;
        }
        if (itemForm.image.length === 0) {
            notify('error', 'At least one item image is required');
            return false;
        }
        if (!itemForm.imageRefId.trim()) {
            notify('error', 'Image reference ID is required');
            return false;
        }
        if (itemForm.video && !itemForm.videoRefId.trim()) {
            notify('error', 'Video reference ID is required');
            return false;
        }
        return true;
    };

    const clearItemForm = () => {
        setItemForm({
            productName: '',
            purchaseType: 'Coins',
            price: '',
            accessType: 'Public',
            itemType: 'Skin',
            skinType: 'IRL',
            isUnlockable: true,
            image: [],
            video: null,
            bundleName: '',
            bundlePrice: '',
            imageRefId: '',
            videoRefId: '',
            subType: '',
            subType2: '',
            seasonPass: 'None',
            seasonPassName: '',
            released: '',
        });
        if (imageInputRef.current) {
            imageInputRef.current.value = '';
        }
        if (videoInputRef.current) {
            videoInputRef.current.value = '';
        }
    };



    return (
        <>
            {isLoading && <GlobalThrobberComponent />}
            <div className="content-panel">
                <h2>Item Category Management</h2>
            <div className="item-category-container">
                {permissions?.write && permissions?.gc_items && (
                  <div className="item-form-section">
                    <h3>Add New Item</h3>
                    {blockUntil > Date.now() / 1000 ? (
                        <div className="blocked-message">
                            You are blocked until {new Date(blockUntil * 1000).toLocaleString()}
                        </div>
                    ) : (
                    <form className="modal-form" onSubmit={e => e.preventDefault()}>
                        <div className="form-section">
                            <h4>Item Details</h4>
                            <label>
                                Product Name
                                <input
                                    type="text"
                                    value={itemForm.productName}
                                    onChange={e => setItemForm({ ...itemForm, productName: e.target.value })}
                                    placeholder="Enter product name"
                                    required
                                />
                            </label>
                            <label>
                                Purchase Type
                                <select
                                    value={itemForm.purchaseType}
                                    onChange={e => {
                                        const newPurchaseType = e.target.value;
                                        setItemForm(prev => ({
                                            ...prev,
                                            purchaseType: newPurchaseType,
                                            seasonPass: newPurchaseType === 'Coins' ? 'None' : prev.seasonPass,
                                            seasonPassName: newPurchaseType === 'Coins' ? '' : prev.seasonPassName,
                                        }));
                                    }}
                                    className="select-field"
                                >
                                    <option value="Coins">Coins</option>
                                    <option value="Purchase">Meta Purchase</option>
                                    <option value="Bundle">Bundle</option>
                                    <option value="Unlockable">Unlockable</option>
                                </select>
                            </label>
                            {itemForm.purchaseType !== 'Unlockable' && itemForm.purchaseType !== 'Bundle' && (
                                <label>
                                    Price
                                    <input
                                        type="text"
                                        value={itemForm.price}
                                        onChange={e => {
                                            const val = e.target.value;
                                            if (/^[\d.]*$/.test(val)) {
                                                setItemForm({ ...itemForm, price: val });
                                            }
                                        }}
                                        placeholder={itemForm.purchaseType === 'Purchase' ? 'Enter price in Dollars' : 'Enter price in coins'}
                                    />
                                </label>
                            )}
                            {itemForm.purchaseType === 'Bundle' && (
                                <>
                                    <label>
                                        Bundle Name
                                        <input
                                            type="text"
                                            value={itemForm.bundleName}
                                            onChange={e => setItemForm({ ...itemForm, bundleName: e.target.value })}
                                            placeholder="Enter bundle name"
                                        />
                                    </label>
                                    <label>
                                        Bundle Price Type
                                        <select
                                            value={itemForm.bundlePriceType}
                                            onChange={e => setItemForm({ ...itemForm, bundlePriceType: e.target.value })}
                                            className="select-field"
                                        >
                                            <option value="Coins">Coins</option>
                                            <option value="Dollars">Dollars</option>
                                            <option value="Both">Both</option>
                                        </select>
                                    </label>
                                    {itemForm.bundlePriceType === 'Both' ? (
                                        <>
                                            <label>
                                                Coin Price
                                                <input
                                                    type="text"
                                                    value={itemForm.bundleCoinPrice}
                                                    onChange={e => {
                                                        const val = e.target.value;
                                                        if (/^\d*$/.test(val)) {
                                                            setItemForm({ ...itemForm, bundleCoinPrice: val });
                                                        }
                                                    }}
                                                    placeholder="Enter price in coins"
                                                />
                                            </label>
                                            <label>
                                                Dollar Price
                                                <input
                                                    type="text"
                                                    value={itemForm.bundleDollarPrice}
                                                    onChange={e => {
                                                        const val = e.target.value;
                                                        if (/^[\d.]*$/.test(val)) {
                                                            setItemForm({ ...itemForm, bundleDollarPrice: val });
                                                        }
                                                    }}
                                                    placeholder="Enter price in Dollars"
                                                />
                                            </label>
                                        </>
                                    ) : (
                                        <label>
                                            Bundle Price
                                            <input
                                                type="text"
                                                value={itemForm.bundlePrice}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    if (itemForm.bundlePriceType === 'Dollars') {
                                                        if (/^[\d.]*$/.test(val)) {
                                                            setItemForm({ ...itemForm, bundlePrice: val });
                                                        }
                                                    } else {
                                                        if (/^\d*$/.test(val)) {
                                                            setItemForm({ ...itemForm, bundlePrice: val });
                                                        }
                                                    }
                                                }}
                                                placeholder={itemForm.bundlePriceType === 'Dollars' ? 'Enter price in Dollars' : 'Enter price in coins'}
                                            />
                                        </label>
                                    )}
                                </>
                            )}
                            <label>
                                Access Type
                                <select
                                    value={itemForm.accessType}
                                    onChange={e => setItemForm({ ...itemForm, accessType: e.target.value })}
                                    className="select-field"
                                >
                                    <option value="public">Public</option>
                                    <option value="limited_event">Event</option>
                                    <option value="pro_only">Pro Only</option>
                                    <option value="role_base">Discord Role</option>
                                </select>
                            </label>
                            <label>
                                Item Type
                                <select
                                    value={itemForm.itemType}
                                    onChange={e => setItemForm({ ...itemForm, itemType: e.target.value, subType: '', subType2: '' })}
                                    className="select-field"
                                >
                                    <option value="Character">Character</option>
                                    <option value="Clothes">Clothes</option>
                                    <option value="Accessories">Accessories</option>
                                    <option value="Tag/Effects">Tag/Effects</option>
                                </select>
                            </label>
                            {itemForm.itemType === 'Character' && (
                                <label>
                                    Character Type
                                    <select
                                        value={itemForm.subType}
                                        onChange={e => setItemForm({ ...itemForm, subType: e.target.value })}
                                        className="select-field"
                                    >
                                        <option value="">None</option>
                                        <option value="Hair">Hair</option>
                                        <option value="Facial Hair">Facial Hair</option>
                                    </select>
                                </label>
                            )}
                            {itemForm.itemType === 'Clothes' && (
                                <label>
                                    Clothes Type
                                    <select
                                        value={itemForm.subType}
                                        onChange={e => setItemForm({ ...itemForm, subType: e.target.value, subType2: '' })}
                                        className="select-field"
                                    >
                                        <option value="">None</option>
                                        <option value="Base">Base</option>
                                        <option value="IRL">IRL</option>
                                        <option value="Hoodie">Hoodie</option>
                                        <option value="Puffer">Puffer</option>
                                        <option value="Suit">Suit</option>
                                        <option value="Jersey">Jersey</option>
                                        <option value="Shirtless">Shirtless</option>
                                        <option value="Mascot">Mascot</option>
                                        <option value="Catchers Gear">Catchers Gear</option>
                                    </select>
                                </label>
                            )}
                            {itemForm.itemType === 'Clothes' && itemForm.subType === 'Mascot' && (
                                <label>
                                    Mascot Type
                                    <select
                                        value={itemForm.subType2}
                                        onChange={e => setItemForm({ ...itemForm, subType2: e.target.value })}
                                        className="select-field"
                                    >
                                        <option value="">None</option>
                                        <option value="Bee">Bee</option>
                                        <option value="Gator">Gator</option>
                                        <option value="Snowman">Snowman</option>
                                    </select>
                                </label>
                            )}
                            {itemForm.itemType === 'Accessories' && (
                                <label>
                                    Accessory Type
                                    <select
                                        value={itemForm.subType}
                                        onChange={e => setItemForm({ ...itemForm, subType: e.target.value, subType2: '' })}
                                        className="select-field"
                                    >
                                        <option value="">None</option>
                                        <option value="Eyes">Eyes</option>
                                        <option value="Head">Head</option>
                                        <option value="Ears">Ears</option>
                                        <option value="Hands">Hands</option>
                                    </select>
                                </label>
                            )}
                            {itemForm.itemType === 'Accessories' && itemForm.subType === 'Eyes' && (
                                <label>
                                    Eye accessory Type
                                    <select
                                        value={itemForm.subType2}
                                        onChange={e => setItemForm({ ...itemForm, subType2: e.target.value })}
                                        className="select-field"
                                    >
                                        <option value="">None</option>
                                        <option value="Shades">Shades</option>
                                        <option value="Glasses">Glasses</option>
                                        <option value="Goggles">Goggles</option>
                                    </select>
                                </label>
                            )}
                            {itemForm.itemType === 'Accessories' && itemForm.subType === 'Head' && (
                                <label>
                                    Head accessory Type
                                    <select
                                        value={itemForm.subType2}
                                        onChange={e => setItemForm({ ...itemForm, subType2: e.target.value })}
                                        className="select-field"
                                    >
                                        <option value="">None</option>
                                        <option value="Beanie">Beanie</option>
                                        <option value="Cap">Cap</option>
                                        <option value="Crown">Headband</option>
                                        <option value="Helmet">Helmet</option>
                                        <option value="Visor">Visor</option>
                                    </select>
                                </label>
                            )}
                            {itemForm.itemType === 'Accessories' && itemForm.subType === 'Ears' && (
                                <label>
                                    Ear accessory Type
                                    <select
                                        value={itemForm.subType2}
                                        onChange={e => setItemForm({ ...itemForm, subType2: e.target.value })}
                                        className="select-field"
                                    >
                                        <option value="">None</option>
                                        <option value="Headphones">Ear Muffs</option>
                                    </select>
                                </label>
                            )}
                            {itemForm.itemType === 'Accessories' && itemForm.subType === 'Hands' && (
                                <label>
                                    Hand accessory Type
                                    <select
                                        value={itemForm.subType2}
                                        onChange={e => setItemForm({ ...itemForm, subType2: e.target.value })}
                                        className="select-field"
                                    >
                                        <option value="">None</option>
                                        <option value="Gloves">Gloves</option>
                                    </select>
                                </label>
                            )}
                            {itemForm.itemType === 'Tag/Effects' && (
                                <label>
                                    Tag/Effect Type
                                    <select
                                        value={itemForm.subType}
                                        onChange={e => setItemForm({ ...itemForm, subType: e.target.value })}
                                        className="select-field"
                                    >
                                        <option value="">None</option>
                                        <option value="Frame">Frame</option>
                                    </select>
                                </label>
                            )}
                            {itemForm.purchaseType !== 'Coins' && (
                                <label>
                                    Season Pass
                                    <select
                                        value={itemForm.seasonPass}
                                        onChange={e => setItemForm({ ...itemForm, seasonPass: e.target.value })}
                                        className="select-field"
                                    >
                                        <option value="None">None</option>
                                        <option value="Season1">Season 1</option>
                                        <option value="Season 2">Season 2</option>
                                    </select>
                                </label>
                            )}
                            {itemForm.seasonPass !== 'None' && (
                                <label>
                                    Season Pass Name
                                    <input
                                        type="text"
                                        value={itemForm.seasonPassName}
                                        onChange={e => setItemForm({ ...itemForm, seasonPassName: e.target.value })}
                                        placeholder="Enter season pass name"
                                    />
                                </label>
                            )}
                            <label>
                                Released
                                <input
                                    type="date"
                                    value={itemForm.released}
                                    onChange={e => setItemForm({ ...itemForm, released: e.target.value })}
                                />
                            </label>
                            <div className="checkbox-row">
                                <input
                                    type="checkbox"
                                    id="isUnlockable"
                                    checked={itemForm.isUnlockable}
                                    onChange={e => setItemForm({ ...itemForm, isUnlockable: e.target.checked })}
                                />
                                <label htmlFor="isUnlockable">Still  Available</label>
                            </div>
                        </div>
                        <div className="form-section media-upload-section">
                            <h4>Media Upload</h4>
                            <div className="media-upload-container">
                                <div className="upload-area">
                                    <div className="upload-item">
                                        <div className="upload-header">
                                            <span className="upload-icon">📷</span>
                                            <span className="upload-title">Item Images</span>
                                            <span className="required-badge">Required</span>
                                        </div>
                                        <div
                                            className="upload-dropzone"
                                            onClick={e => {
                                                // Only open file dialog when clicking on the dropzone background
                                                if (e.target === e.currentTarget && imageInputRef.current) {
                                                    imageInputRef.current.click();
                                                }
                                            }}
                                            onDragOver={e => e.preventDefault()}
                                            onDrop={e => {
                                                e.preventDefault();
                                                handleImageUpload({ target: { files: e.dataTransfer.files } });
                                            }}
                                        >
                                            <input
                                                ref={imageInputRef}
                                                type="file"
                                                accept="image/jpeg,image/png"
                                                multiple
                                                onChange={handleImageUpload}
                                                className="upload-input"
                                                id="image-upload"
                                            />
                                            <label htmlFor="image-upload" className="upload-label">
                                                <div className="upload-placeholder">
                                                    <div className="upload-icon-large">📁</div>
                                                    <div className="upload-text">
                                                        <strong>Click to upload</strong> or drag and drop
                                                    </div>
                                                    <div className="upload-hint">PNG, JPG, GIF up to 10MB</div>
                                                </div>
                                            </label>
                                        </div>
                                        <div className="image-previews">
                                            {itemForm.image.map((img, index) => (
                                                <div key={index} className="upload-preview">
                                                    <div className="preview-image">
                                                        <img src={URL.createObjectURL(img)} alt={`Preview ${index}`} />
                                                    </div>
                                                    <div className="preview-info">
                                                        <div className="preview-name">{img.name}</div>
                                                        <div className="preview-size">{(img.size / 1024).toFixed(1)} KB</div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="remove-file-btn"
                                                        onClick={() => handleRemoveImage(index)}
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="upload-meta">
                                            <input
                                                type="text"
                                                value={itemForm.imageRefId}
                                                onChange={e => setItemForm({ ...itemForm, imageRefId: e.target.value })}
                                                placeholder="Image reference ID"
                                                className="ref-id-input"
                                            />
                                        </div>
                                    </div>
                                    <div className="upload-item">
                                        <div className="upload-header">
                                            <span className="upload-icon">🎬</span>
                                            <span className="upload-title">Item Video</span>
                                            <span className="optional-badge">Optional</span>
                                        </div>
                                        <div className="upload-dropzone"
                                             onDragOver={e => e.preventDefault()}
                                             onDrop={e => {
                                                 e.preventDefault();
                                                 const files = Array.from(e.dataTransfer.files);
                                                 const videoFile = files.find(f => f.type.startsWith('video/'));
                                                 if (videoFile) setItemForm({ ...itemForm, video: videoFile });
                                             }}>
                                            <input
                                                ref={videoInputRef}
                                                type="file"
                                                accept="video/*"
                                                onChange={e => setItemForm({ ...itemForm, video: e.target.files[0] })}
                                                className="upload-input"
                                                id="video-upload"
                                            />
                                            <label htmlFor="video-upload" className="upload-label">
                                                {itemForm.video ? (
                                                    <div className="upload-preview">
                                                        <div className="preview-video">
                                                            <video src={URL.createObjectURL(itemForm.video)} controls />
                                                        </div>
                                                        <div className="preview-info">
                                                            <div className="preview-name">{itemForm.video.name}</div>
                                                            <div className="preview-size">{(itemForm.video.size / 1024 / 1024).toFixed(1)} MB</div>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="remove-file-btn"
                                                            onClick={e => {
                                                                e.preventDefault();
                                                                setItemForm({ ...itemForm, video: null });
                                                            }}
                                                        >
                                                            ✕
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="upload-placeholder">
                                                        <div className="upload-icon-large">🎥</div>
                                                        <div className="upload-text">
                                                            <strong>Click to upload</strong> or drag and drop
                                                        </div>
                                                        <div className="upload-hint">MP4, WebM, MOV up to 50MB</div>
                                                    </div>
                                                )}
                                            </label>
                                        </div>
                                        <div className="upload-meta">
                                            <input
                                                type="text"
                                                value={itemForm.videoRefId}
                                                onChange={e => setItemForm({ ...itemForm, videoRefId: e.target.value })}
                                                placeholder="Video reference ID"
                                                className="ref-id-input"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="form-actions">
                            <button
                                type="button"
                                className="primary-btn"
                                onClick={async (e) => {
                                    e.preventDefault();
                                    if (!(permissions?.write && permissions?.gc_items)) {
                                        notify('error', 'You do not have permission to create items');
                                        return;
                                    }
                                    if (!validateItemForm()) {
                                        return;
                                    }
                                    setIsLoading(true);
                                    try {
                                        if (!itemForm.imageRefId.trim()) throw new Error("Image reference ID is required");

                                        const imagePutUrl = await getPresignedUrl(itemForm.imageRefId);
                                        await Promise.all(itemForm.image.map(file => uploadFile(imagePutUrl, file)));

                                        // upload video if its added by the user
                                        if (itemForm.video) {
                                            if (!itemForm.videoRefId.trim()) throw new Error("Video reference ID is required");

                                            const videoPutUrl = await getPresignedUrl(itemForm.videoRefId);
                                            await uploadFile(videoPutUrl, itemForm.video);
                                        }

                                        const payload = {
                                            ...itemForm,
                                            image: undefined,
                                            video: undefined,
                                        };

                                        const created = await createItemOnServer(payload);

                                        // Fetch full item record so all defaults / URLs are populated
                                        let serverItem = created;
                                        try {
                                            const res = await fetch(`${API_BASE}/${API_VERSION}/private/gym-class-item-category/${created.item_id}`, {
                                                method: 'GET',
                                                mode: 'cors'
                                            });
                                            if (res.ok) {
                                                serverItem = await res.json();
                                            }
                                        } catch (_) {
                                            // fall back to original 'created' object
                                        }

                                        // Ensure media URLs present if backend omitted
                                        if (!serverItem.imageUrl && itemForm.imageRefId.trim()) {
                                            serverItem.imageUrl = `${API_BASE}/${API_VERSION}/private/gym-class-item-category/media/${itemForm.imageRefId}`;
                                        }
                                        if (!serverItem.videoUrl && itemForm.videoRefId.trim()) {
                                            serverItem.videoUrl = `${API_BASE}/${API_VERSION}/private/gym-class-item-category/media/${itemForm.videoRefId}`;
                                        }

                                        setItemList(prev => [...prev, serverItem]);
                                        clearItemForm();
                                        notify('success', 'Item added successfully');
                                        if (user) {
                                            localStorage.setItem(`last_item_${user.id}`, JSON.stringify(serverItem));
                                        }
                                    } catch (err) {
                                        if (err.timeout_expires) {
                                            setBlockUntil(err.timeout_expires);
                                            localStorage.setItem(`block_until_${user.id}`, err.timeout_expires);
                                            notify('error', `You have been blocked until ${new Date(err.timeout_expires * 1000).toLocaleString()}`);
                                        } else {
                                            notify('error', err.message || err.error || 'An error occurred');
                                        }
                                    } finally {
                                        setIsLoading(false);
                                    }
                                }}
                            >
                                Add Item
                            </button>
                            <button
                                type="button"
                                className="secondary-btn"
                                onClick={() => {
                                    setItemForm({
                                        productName: '',
                                        purchaseType: 'Coins',
                                        price: '',
                                        accessType: 'Public',
                                        itemType: 'Skin',
                                        skinType: 'IRL',
                                        isUnlockable: true,
                                        image: null,
                                        video: null
                                    });
                                }}
                            >
                                Clear Form
                            </button>
                        </div>
                    </form>
                    )}
                  </div>
                )}
                <div className="item-list-section">
                    <h3>Items List ({filteredAndSortedItems.length}{itemList.length !== filteredAndSortedItems.length ? ` of ${itemList.length}` : ''})</h3>
                    <div className="item-controls">
                        <div className="search-bar">
                            <input
                                type="text"
                                placeholder="Search items"
                                value={itemSearch}
                                onChange={e => setItemSearch(e.target.value)}
                                className="item-search"
                            />
                            {itemSearch && (
                                <button className="clear-search" onClick={() => setItemSearch('')}>✕</button>
                            )}
                        </div>
                        <div className="filter-controls">
                            <select
                                value={itemFilter.itemType}
                                onChange={e => setItemFilter({ ...itemFilter, itemType: e.target.value })}
                                className="filter-select"
                            >
                                <option value="">All Types</option>
                                <option value="Character">Character</option>
                                <option value="Clothes">Clothes</option>
                                <option value="Accessories">Accessories</option>
                                <option value="Tag/Effects">Tag/Effects</option>
                            </select>
                            <select
                                value={itemFilter.purchaseType}
                                onChange={e => setItemFilter({ ...itemFilter, purchaseType: e.target.value })}
                                className="filter-select"
                            >
                                <option value="">All Purchases</option>
                                <option value="Coins">Coins</option>
                                <option value="Purchase">Meta Purchase</option>
                                <option value="Bundle">Bundle</option>
                                <option value="Unlockable">Unlockable</option>
                            </select>
                            <select
                                value={itemFilter.accessType}
                                onChange={e => setItemFilter({ ...itemFilter, accessType: e.target.value })}
                                className="filter-select"
                            >
                                <option value="">All Access</option>
                                <option value="public">Public</option>
                                <option value="limited_event">Event</option>
                                <option value="pro_only">Pro Only</option>
                                <option value="role_base">Discord Role</option>
                            </select>
                        </div>
                    </div>
                    {filteredAndSortedItems.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-icon">🎮</div>
                            <p>{itemList.length === 0 ? 'No items added yet' : 'No items match your filters'}</p>
                            {itemList.length > 0 && (
                                <button className="secondary-btn" onClick={clearItemFilters}>
                                    Clear Filters
                                </button>
                            )}
                        </div>
                    ) : (
                        <>
                            <div
                                className="items-grid-container"
                                ref={itemListRef}
                                onScroll={handleScroll}
                                style={{
                                    height: `${containerHeight}px`,
                                    overflowY: 'auto',
                                    position: 'relative'
                                }}
                            >
                                {filteredAndSortedItems.length > 100 ? (
                                    <div
                                        className="items-grid virtualized"
                                        style={{
                                            height: `${filteredAndSortedItems.length * itemHeight}px`,
                                            position: 'relative'
                                        }}
                                    >
                                        {virtualizedItems.map(item => (
                                            <div
                                                key={item.id}
                                                className="item-card"
                                                style={{
                                                    position: 'absolute',
                                                    top: `${item.virtualIndex * itemHeight}px`,
                                                    left: 0,
                                                    right: 0,
                                                    height: `${itemHeight - 20}px`
                                                }}
                                            >
                                                <div
                                                    className="item-media"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                    }}
                                                >
                                                    {(() => {
                                                        const mediaItems = [];
                                                        if (item.imageUrl) mediaItems.push({ type: 'image', url: item.imageUrl });
                                                        if (item.videoUrl) mediaItems.push({ type: 'video', url: item.videoUrl });

                                                        return <CarouselComponent mediaItems={mediaItems} productName={item.productName} />;
                                                    })()}
                                                </div>
                                                <div className="item-details">
                                                    <h4 className="item-name">{item.productName}</h4>
                                                    <div className="item-info">
                                                        <div className="item-info-group">
                                                            <div className="item-info-label">Type</div>
                                                            <span className={`item-type ${(item.itemType || '').toLowerCase()}`}>
                                                                {item.itemType}
                                                                {item.subType && (
                                                                    <span className="sub-type"> - {item.subType}</span>
                                                                )}
                                                                {item.subType2 && (
                                                                    <span className="sub-type2"> - {item.subType2}</span>
                                                                )}
                                                            </span>
                                                        </div>
                                                        <div className="item-info-group">
                                                            <div className="item-info-label">Purchase</div>
                                                            <span className={`purchase-type ${(item.purchaseType || '').toLowerCase()}`}>
                                                                {item.purchaseType}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="item-details-row">
                                                        <div className="price-section">
                                                            <div className="item-info-label">Price</div>
                                                            <span className="item-price">
                                                                {item.purchaseType === 'Bundle' ?
                                                                    (item.bundlePriceType === 'Both' ? `${item.bundleCoinPrice} coins / $${item.bundleDollarPrice}` : item.bundlePrice) :
                                                                    item.price ? `${item.price} ${item.purchaseType === 'Purchase' ? 'USD' : 'coins'}` : 'Free'}
                                                            </span>
                                                        </div>
                                                        <div className="access-section">
                                                            <div className="item-info-label">Access</div>
                                                            <span className={`access-type ${(item.accessType || '').toLowerCase()}`}>
                                                                {item.accessType}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    {item.seasonPass !== 'None' && (
                                                        <div className="item-season-pass">
                                                            <div className="season-pass-label">Season Pass</div>
                                                            <div>{item.seasonPass} - {item.seasonPassName}</div>
                                                        </div>
                                                    )}
                                                    <div className="item-status">
                                                        <div className="status-label">Availability</div>
                                                        <span className={`availability ${item.isUnlockable ? 'available' : 'unavailable'}`}>
                                                            {item.isUnlockable ? 'Available' : 'Unavailable'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="items-grid standard">
                                        {(filteredAndSortedItems.length <= 50 ? filteredAndSortedItems : paginatedItems).map(item => (
                                            <div key={item.id} className="item-card">
                                                <div
                                                    className="item-media"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                    }}
                                                >
                                                    {(() => {
                                                        const mediaItems = [];
                                                        if (item.imageUrl) mediaItems.push({ type: 'image', url: item.imageUrl });
                                                        if (item.videoUrl) mediaItems.push({ type: 'video', url: item.videoUrl });

                                                        return <CarouselComponent mediaItems={mediaItems} productName={item.productName} />;
                                                    })()}
                                                </div>
                                                <div className="item-details">
                                                    <h4 className="item-name">{item.productName}</h4>
                                                    <div className="item-info">
                                                        <div className="item-info-group">
                                                            <div className="item-info-label">Type</div>
                                                            <span className={`item-type ${(item.itemType || '').toLowerCase()}`}>
                                                                {item.itemType}
                                                                {item.subType && (
                                                                    <span className="sub-type"> - {item.subType}</span>
                                                                )}
                                                                {item.subType2 && (
                                                                    <span className="sub-type2"> - {item.subType2}</span>
                                                                )}
                                                            </span>
                                                        </div>
                                                        <div className="item-info-group">
                                                            <div className="item-info-label">Purchase</div>
                                                            <span className={`purchase-type ${(item.purchaseType || '').toLowerCase()}`}>
                                                                {item.purchaseType}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="item-details-row">
                                                        <div className="price-section">
                                                            <div className="item-info-label">Price</div>
                                                            <span className="item-price">
                                                                {item.purchaseType === 'Bundle' ?
                                                                    (item.bundlePriceType === 'Both' ? `${item.bundleCoinPrice} coins / $${item.bundleDollarPrice}` : item.bundlePrice) :
                                                                    item.price ? `${item.price} ${item.purchaseType === 'Purchase' ? 'USD' : 'coins'}` : 'Free'}
                                                            </span>
                                                        </div>
                                                        <div className="access-section">
                                                            <div className="item-info-label">Access</div>
                                                            <span className={`access-type ${(item.accessType || '').toLowerCase()}`}>
                                                                {item.accessType}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    {item.seasonPass !== 'None' && (
                                                        <div className="item-season-pass">
                                                            <div className="season-pass-label">Season Pass</div>
                                                            <div>{item.seasonPass} - {item.seasonPassName}</div>
                                                        </div>
                                                    )}
                                                    <div className="item-status">
                                                        <div className="status-label">Availability</div>
                                                        <span className={`availability ${item.isUnlockable ? 'available' : 'unavailable'}`}>
                                                            {item.isUnlockable ? 'Available' : 'Unavailable'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {itemPage * itemsPerPage < filteredAndSortedItems.length && (
                                <div className="pagination">
                                    <button
                                        className="load-more-button"
                                        onClick={() => {
                                            setItemPage(p => p + 1);
                                            // Scroll back to top of the item list container
                                            if (itemListRef.current) {
                                                itemListRef.current.scrollTo({ top: 0, behavior: 'smooth' });
                                            } else {
                                                window.scrollTo({ top: 0, behavior: 'smooth' });
                                            }
                                        }}
                                    >
                                        Load More ({filteredAndSortedItems.length - itemPage * itemsPerPage} remaining)
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            </div>
        </>
    );
};

export default ItemCatalogComponent;
